import { UserType } from "@whatslly/types/User";
import { FrontLogger } from "../frontLogger/frontLogger";

// Base parameters that all tracking methods should have
export type BaseTrackParams<T> = {
  userId?: string; // Optional for events that don't require user identification
} & T;

export type UserProperties = {
  UserID: string;
  OrgID: string;
  Username?: string;
  Email?: string;
  "User Language"?: string;
};

// Base types for consistent event tracking
export type DefaultEventProperties = {
  OrgID: string;
  Environment: "production" | "staging" | "development";
  Source: string;
};

// Login/Logout Events
export type TrackLoginParams = BaseTrackParams<{
  userId: string;
  authSource: string; // SSO, Gmail, etc
  email?: string;
  username?: string;
  language?: string;
}>;

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type TrackLogoutParams = BaseTrackParams<{}>;

// User Management Events
export type TrackUsersImportedParams = BaseTrackParams<{
  userCount: number;
  remoteSync: number;
  success: boolean;
  ignoredCount: number;
}>;

export type TrackUserInvitedParams = BaseTrackParams<{
  success: boolean;
  inviteToPlatform: boolean;
  inviteToApplications: boolean;
  inviteToRemoteSync: boolean;
}>;

export type TrackUserPermissionUpdatedParams = BaseTrackParams<{
  oldPermissions: string;
  newPermissions: string;
  userId: string;
  userName: string;
}>;

// Export Events
export type TrackCsvExportedParams = BaseTrackParams<{
  tableName: string;
}>;

export type TrackDashboardCsvExportedParams = BaseTrackParams<{
  dashboardTab: string;
  tableName: string;
}>;

// Dashboard Events
export type TrackDashboardViewedParams = BaseTrackParams<{
  dashboardName: string;
}>;

// Conversations page events
export type TrackConversationsCsvExportClickedParams = {
  tab: string;
};

export type TrackConversationsExportRequestedParams = {
  tab: string;
  rangeDays: number;
  estimatedMessages?: number;
};

export type TrackConversationsExportDownloadedParams = {
  part: number;
  parts: number;
};

export type TrackConversationsDateFilterAppliedParams = {
  fromDate: string;
  toDate: string;
  rangeDays: number;
};

export type TrackConversationsUserFilterAppliedParams = {
  filterType: "users" | "phones";
  selectedCount: number;
};

export type MixpanelInitParams = {
  token: string;
  source: string;
  orgId: string;
  user?: UserType;
  logger?: FrontLogger;
  etoken?: string;
  pod?: string;
  isAdmin?: boolean;
};

// Builder Pattern Types
export interface IMixpanelEventBuilder {
  setEventName(name: string): IMixpanelEventBuilder;
  setOrgId(orgId: string): IMixpanelEventBuilder;
  addProperty(key: string, value: any): IMixpanelEventBuilder;
  addProperties(properties: Record<string, any>): IMixpanelEventBuilder;
  setCallback(callback: () => void): IMixpanelEventBuilder;
  track(): void;
}

export type TrackEventParams = {
  eventName: string;
  properties: Record<string, any>;
  orgId: string;
  callback?: () => void;
};

// Mixpanel Service Interface
export interface IMixpanelService {
  init({ token, user, logger, source }: MixpanelInitParams): void;
  // Builder pattern method
  createEventBuilder(): IMixpanelEventBuilder;
  // Authentication events
  trackLogin(params: TrackLoginParams): void;
  trackLogout(params: TrackLogoutParams): void;
  // User management events
  trackUsersImported(params: TrackUsersImportedParams): void;
  trackUserInvited(params: TrackUserInvitedParams): void;
  trackUserPermissionUpdated(params: TrackUserPermissionUpdatedParams): void;
  setOrgId(orgId?: string): void;
  // Export events
  trackCsvExported(params: TrackCsvExportedParams): void;
  // Dashboard events
  trackDashboardCsvExported(params: TrackDashboardCsvExportedParams): void;
  trackDashboardViewed(params: TrackDashboardViewedParams): void;
  // Conversations page events
  trackConversationsCsvExportClicked(params: TrackConversationsCsvExportClickedParams): void;
  trackConversationsExportRequested(params: TrackConversationsExportRequestedParams): void;
  trackConversationsExportDownloaded(params: TrackConversationsExportDownloadedParams): void;
  trackConversationsDateFilterApplied(params: TrackConversationsDateFilterAppliedParams): void;
  trackConversationsUserFilterApplied(params: TrackConversationsUserFilterAppliedParams): void;
}

// Mixpanel Service Symbol for dependency injection
export const MixpanelServiceSymbol = Symbol.for("MixpanelService");
